import { useMemo } from "react"
import {
  Typography,
  CardMedia,
  CardContent,
  Stack,
  Box,
  useTheme,
  Card,
  Chip,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { LocationIcon } from "components/icons"

import { getIcon } from "utils/icon"
import {
  getFeatureDetail,
  IEventDetail,
  StatusByDateRange,
} from "utils/feature"
import { getDisplayDateTime } from "utils/date"
import { useTranslation } from "providers/i18n"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { LeftToRightWrapper } from "components/LeftToRightWrapper"

export const VerticalEventCard = ({ event, dense = false, sx = {} }) => {
  const { t } = useTranslation()
  const {
    name,
    coverImage,
    venueLogo,
    startDate,
    endDate,
    hasLocation,
    isAd,
    status,
  } = getFeatureDetail(event) as IEventDetail

  const durationText = useMemo(
    () => getDisplayDateTime(startDate, endDate),
    [startDate, endDate]
  )

  const theme = useTheme()
  const { styleOverrides } = theme.components?.[`VerticalEventCard`] || {}
  const {
    root,
    cardContent,
    cardMedia,
    cardTitle,
    iconContainer,
    venueLogoContainer,
    cardContentStack,
    durationContainer,
    duration,
    cardInfoContainer,
    chipContainer,
    highlightTextChip,
    statusTextContainer,
    statusText,
    locationInfo,
  } = styleOverrides

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  return (
    <Card
      sx={deepmerge(root, sx)}
      className={dense ? "MuiCardContent-dense" : ""}
    >
      <CardMedia sx={cardMedia} image={coverImage}>
        <Box sx={chipContainer}>
          {isAd && (
            <Chip label={t("Highlight")} size="small" sx={highlightTextChip} />
          )}
        </Box>
        {status === StatusByDateRange.Upcoming && (
          <Box sx={statusTextContainer}>
            <Typography variant="caption" component="p" sx={statusText}>
              {t("Upcoming")}
            </Typography>
          </Box>
        )}
      </CardMedia>

      <CardContent sx={cardContent} className="VerticalEventCard-cardContent">
        <Stack spacing={1} sx={cardContentStack}>
          {venueLogo && (
            <Box sx={venueLogoContainer}>
              <CardMedia component="img" image={venueLogo} />
            </Box>
          )}
          <Box sx={durationContainer}>
            <LeftToRightWrapper>
              <Typography
                className="VerticalEventCard-durationText"
                sx={duration}
                component="p"
                variant="subTitle"
              >
                {durationText}
              </Typography>
            </LeftToRightWrapper>
          </Box>
          <Typography
            sx={cardTitle}
            variant="subTitle"
            component="div"
            color="text.primary"
            className="VerticalEventCard-cardTitleText"
          >
            {t(name)}
          </Typography>
        </Stack>
        <Box
          sx={{
            ...cardInfoContainer,
            visibility: hasLocation ? "visible" : "hidden",
          }}
        >
          <Box sx={iconContainer} color="text.secondary">
            {locationIcon}
          </Box>
          <LocationInfo
            feature={event}
            component="p"
            variant="subTitle"
            color="text.secondary"
            sx={locationInfo}
          />
        </Box>
      </CardContent>
    </Card>
  )
}
